/* eslint-disable no-nested-ternary */
import React, {useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {Col, Row, Container} from 'styled-bootstrap-grid'
import {palette} from 'styled-tools'
import router from 'next/router'
import CloseLineIcon from 'remixicon-react/CloseLineIcon'
import {formatNumber} from 'src/utils/numbers'
import {PSmall} from 'src/components/typography'
import {Button} from 'components/buttons'
import Loader from 'src/components/common/Loader'
import {BlockTitle, SpacingWrapper} from 'components/layout'

import CartOverview from 'components/cart/CartOverview'
import PageContainer from 'components/layout/PageContainer'
import EmptyCartButton from 'components/cart/EmptyCartButton'
import {CheckoutLineCard} from 'components/cart/CheckoutLineCard'

import {paths} from 'src/utils/paths'
import type {GetStaticProps} from 'next'
import {makeSubscriptionProps} from 'src/api/datocms/utils'
import {pageQuery} from 'src/api/datocms/queries'
import {getPluralOrSingular} from 'src/utils/icelandicQuantity'
import {Breadcrumbs} from 'src/components/products'
import {DEFAULT_REVALIDATE_TIME} from 'src/const'
// import {CartShipmentPricingAlert} from 'src/components/cart/CartShipmentPricingAlert'
import {theme} from 'src/themes'
import {useCheckout} from 'src/context/CheckoutContext'
import {EmptyCart} from 'src/components/checkout'
import {PageLoading} from 'src/components/genericPage'
import {useUser} from 'src/context/UserContext'
import {useOverlay} from 'src/context/OverlayContext'
import {isSSR} from 'src/utils/isSSR'
import {isNotNull} from 'src/utils/isNotNull'

interface OverViewContainerProps {
  scrollValue: number
  productHeight: number
  cartHeight: number
}

const Divider = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 38px;
  @media (max-width: ${theme.screenSize.sizeMD}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`
const OverviewContainer = styled.div<OverViewContainerProps>`

  

  @media (min-width: ${theme.screenSize.sizeMD}) {
    width: 23vw;

    position: ${(props) =>
      props.scrollValue >= props.productHeight - props.cartHeight
        ? 'absolute'
        : 'fixed'};
    top: ${(props) =>
      props.scrollValue >= props.productHeight - props.cartHeight
        ? `${props.productHeight - props.cartHeight}px`
        : '30vh'};
  }
  .input-container {
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
    gap: 5px;
    .submit-button {
      min-width: 40px;
    }
  }
  .voucher-box {
    border: 1px solid ${palette('black20')};
    padding: 10px;
    width: fit-content;
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
  }

  @media screen and (max-width: 1500px) {
    .input-container {
      flex-direction: column;
    }
  @media screen and (max-width: 768px) {
    .input-container {
      flex-direction: row;
    }

`
const Content = styled.div``
export const getStaticProps: GetStaticProps = async ({preview = false}) => ({
  props: {
    pageSubscription: await makeSubscriptionProps(preview, pageQuery, true),
  },
  revalidate: DEFAULT_REVALIDATE_TIME,
})

export default function Karfa(): JSX.Element {
  const {
    checkout,
    checkoutLoading,
    updateCheckoutVoucher,
    removeCheckoutVoucher,
  } = useCheckout()
  const {user} = useUser()
  const {setSideDrawerIsOpen, sideDrawerIsOpen} = useOverlay()
  const hasPushedButtonRef = useRef<boolean>(false)
  const [voucherLoad, setVoucherLoad] = useState<Record<string, boolean>>({
    add: false,
    remove: false,
  })

  const [scrollValue, setScrollValue] = useState<number>(0)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const productsRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cartRef = useRef<any>(null)

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleScroll = () => {
    setScrollValue(window.scrollY)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (!sideDrawerIsOpen && user && hasPushedButtonRef.current) {
      hasPushedButtonRef.current = false
      router.push(paths.checkout)
    }
  }, [sideDrawerIsOpen, user])
  const handleChange = useCallback(() => {
    if (user) {
      router.push(paths.checkout)
      return
    }
    hasPushedButtonRef.current = true
    setSideDrawerIsOpen(true)
  }, [setSideDrawerIsOpen, user])

  if (!isSSR && checkoutLoading) {
    return <PageLoading />
  }

  const handleVoucherSubmit = (code: string): void => {
    setVoucherLoad({...voucherLoad, add: true})
    updateCheckoutVoucher(code).then(() => {
      setVoucherLoad({...voucherLoad, add: false})
      ;(document.getElementById('voucher-input') as HTMLInputElement).value = ''
    })
  }

  const handleVoucherRemove = (code: string): void => {
    setVoucherLoad({...voucherLoad, remove: true})
    removeCheckoutVoucher(code).then(() => {
      setVoucherLoad({...voucherLoad, remove: false})
    })
  }

  return (
    <PageContainer title="Karfa">
      <Container>
        <Breadcrumbs
          breadcrumbs={[
            {title: 'Forsíða', href: paths.home},
            {title: 'Karfan mín', href: paths.cart},
          ]}
        />
        <SpacingWrapper spacing={50}>
          <BlockTitle title="Karfan mín" />

          {!checkout?.lines?.length ? (
            <EmptyCart />
          ) : (
            <>
              <Row className="row">
                <Col md={8} ref={productsRef}>
                  <Divider>
                    <div>
                      <span>
                        {getPluralOrSingular({
                          plural: 'vörur',
                          singular: 'vara',
                          value: checkout.quantity,
                        })}{' '}
                        í körfu
                      </span>
                      <span> / </span>
                      {checkout.subtotalPrice?.gross.amount ? (
                        <span style={{fontWeight: 600}}>
                          {formatNumber(checkout.subtotalPrice.gross.amount)}{' '}
                          kr.
                        </span>
                      ) : null}
                    </div>

                    <EmptyCartButton />
                  </Divider>
                  {/* <CartShipmentPricingAlert /> */}
                  <Content>
                    {(checkout?.lines || []).filter(isNotNull).map((line) => (
                      <CheckoutLineCard key={line.id} line={line} />
                    ))}
                  </Content>
                </Col>

                <Col md={3} mdOffset={1}>
                  <OverviewContainer
                    cartHeight={cartRef?.current?.offsetHeight || 0}
                    productHeight={productsRef?.current?.offsetHeight || 0}
                    scrollValue={scrollValue}
                    ref={cartRef}
                  >
                    <div className="input-container">
                      <input
                        placeholder="Voucher Code"
                        name="code"
                        id="voucher-input"
                        type="text"
                        style={{padding: '10px'}}
                      />
                      <Button
                        className="submit-button"
                        onClick={() => {
                          const code = (document.getElementById(
                            'voucher-input',
                          ) as HTMLInputElement).value
                          if (code) {
                            handleVoucherSubmit(code)
                          }
                        }}
                      >
                        {!voucherLoad.add ? 'Submit' : <Loader />}
                      </Button>
                    </div>
                    {checkout.voucherCode && (
                      <div className="voucher-box">
                        <PSmall>{checkout.voucherCode}</PSmall>
                        {voucherLoad.remove ? (
                          <Loader />
                        ) : (
                          <CloseLineIcon
                            onClick={() => {
                              if (checkout.voucherCode)
                                handleVoucherRemove(checkout.voucherCode)
                            }}
                          />
                        )}
                      </div>
                    )}
                    <CartOverview />

                    <Button onClick={handleChange} style={{width: '100%'}}>
                      Ganga frá kaupum
                    </Button>
                  </OverviewContainer>
                </Col>
              </Row>
            </>
          )}
        </SpacingWrapper>
      </Container>
    </PageContainer>
  )
}
